import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, FileSignature, CheckCircle2, AlertTriangle, Eraser, FileText, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple signature canvas — supports mouse + touch
const SignaturePad = React.forwardRef(({ disabled }, ref) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const hasContentRef = useRef(false);

  const getCanvasContext = () => {
    const c = canvasRef.current;
    return c ? c.getContext('2d') : null;
  };

  const sizeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, rect.width) * dpr;
    canvas.height = Math.max(1, rect.height) * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = '#0f172a';
    // White background so the PNG isn't transparent when embedded in PDF
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  };

  useEffect(() => {
    sizeCanvas();
    const onResize = () => sizeCanvas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const isTouch = e.touches && e.touches[0];
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
  };

  const move = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const ctx = getCanvasContext();
    if (!ctx) return;
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    hasContentRef.current = true;
  };

  const end = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    hasContentRef.current = false;
  };

  React.useImperativeHandle(ref, () => ({
    isEmpty: () => !hasContentRef.current,
    getDataUrl: () => (canvasRef.current ? canvasRef.current.toDataURL('image/png') : null),
    clear,
  }));

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        className="w-full h-44 sm:h-56 bg-white border-2 border-dashed border-slate-300 rounded-lg cursor-crosshair touch-none"
        style={{ touchAction: 'none' }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <button
        type="button"
        onClick={clear}
        disabled={disabled}
        className="absolute top-2 right-2 inline-flex items-center gap-1 text-xs bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-600 hover:bg-slate-50"
        data-testid="signature-clear"
      >
        <Eraser className="h-3 w-3" /> Effacer
      </button>
    </div>
  );
});
SignaturePad.displayName = 'SignaturePad';

const FieldRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 text-sm py-1.5">
    <span className="text-slate-500 sm:w-44 flex-shrink-0">{label}</span>
    <span className="text-slate-800 font-medium">{value || '—'}</span>
  </div>
);

const Article = ({ num, title, children }) => (
  <div className="border-l-2 border-blue-200 pl-3">
    <h4 className="font-semibold text-slate-800 text-sm mb-1">
      <span className="text-blue-700">Article {num}</span> — {title}
    </h4>
    <div className="text-xs sm:text-sm text-slate-600 leading-relaxed">{children}</div>
  </div>
);

export default function ContractSign() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);
  const [error, setError] = useState('');
  const padRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/contracts/public/${token}`);
        if (data.already_signed) {
          setSigned(true);
          setContract(data);
        } else {
          setContract(data);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || 'Lien invalide ou expiré');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const submit = async () => {
    if (!agree1 || !agree2) {
      toast.error('Veuillez accepter les deux mentions de consentement');
      return;
    }
    if (padRef.current?.isEmpty()) {
      toast.error('Veuillez tracer votre signature');
      return;
    }
    const dataUrl = padRef.current.getDataUrl();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contracts/public/${token}/sign`, {
        signature_image: dataUrl,
      });
      setSigned(true);
      toast.success('Contrat signé avec succès. Une copie vous a été envoyée par email.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Lien invalide</h1>
          <p className="text-slate-600 text-sm">{error}</p>
          <p className="text-slate-400 text-xs mt-4">
            Si vous pensez qu'il s'agit d'une erreur, contactez MA CONCIERGERIE BRETONNE.
          </p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Contrat signé</h1>
          <p className="text-slate-600">
            Merci {contract?.prestataire_nom?.split(' ')[0] || ''}. Votre contrat est enregistré et une
            copie PDF vous a été envoyée par e-mail.
          </p>
          <p className="text-slate-400 text-xs mt-6">MA CONCIERGERIE BRETONNE — PlayMCB</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-6 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <img
            src="/logo-mcb-footer.png"
            alt="MA CONCIERGERIE BRETONNE"
            className="h-12 mx-auto mb-3"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center justify-center gap-2">
            <FileSignature className="h-7 w-7 text-blue-600" />
            Signature de votre contrat
          </h1>
          <p className="text-slate-500 text-sm mt-2">
            Veuillez relire les informations puis signer électroniquement ci-dessous.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6 mb-4">
          <h2 className="font-semibold text-slate-700 mb-3 text-sm uppercase tracking-wide">
            Informations du prestataire
          </h2>
          <FieldRow label="Civilité" value={contract?.civilite} />
          <FieldRow label="Nom complet" value={contract?.prestataire_nom} />
          <FieldRow label="Date de naissance" value={contract?.prestataire_birth_date} />
          <FieldRow label="Lieu de naissance" value={contract?.prestataire_birth_place} />
          <FieldRow label="SIRET" value={contract?.prestataire_siret} />
          <FieldRow label="Adresse" value={contract?.prestataire_address} />
          <FieldRow label="E-mail" value={contract?.prestataire_email} />
          <FieldRow label="Entrée en vigueur" value={contract?.contract_start_date} />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6 mb-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">
              Conditions du contrat
            </h2>
            <a
              href={`${API}/contracts/public/${token}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full px-3 py-1.5 transition-colors"
              data-testid="contract-pdf-link"
            >
              <FileText className="h-3.5 w-3.5" />
              Voir le PDF complet
            </a>
          </div>

          <p className="text-xs text-slate-500 mb-3">
            Veuillez lire attentivement les 22 articles ci-dessous avant de signer. Le PDF complet est également téléchargeable.
          </p>

          <details className="group rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-3" data-testid="contract-articles-toggle">
            <summary className="cursor-pointer text-sm font-semibold text-slate-700 flex items-center justify-between list-none">
              <span>📖 Lire les 22 articles du contrat</span>
              <ChevronDown className="h-4 w-4 text-slate-500 transition-transform group-open:rotate-180" />
            </summary>
            <div className="mt-4 space-y-4 text-sm text-slate-700 leading-relaxed max-h-[55vh] overflow-y-auto pr-1">

              <Article num="1" title="Objet du contrat">
                Définir les modalités selon lesquelles le PRESTATAIRE assure l'entretien et le nettoyage des logements attribués via <b>PlayMCB</b> (prix, adresse et description figurent dans la fiche logement et les commentaires de chaque mission).
              </Article>

              <Article num="2" title="Définition du service">
                Le PRESTATAIRE s'engage à :
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Assurer le nettoyage complet du logement, dans le respect des normes de propreté et d'hygiène ;</li>
                  <li>Changer draps, housses, taies, serviettes, tapis de bain et torchons (fournis par le BÉNÉFICIAIRE), les laver et les remettre en place ;</li>
                  <li>Mettre à disposition savon, gel douche, liquide vaisselle, papier toilette (fournis par le BÉNÉFICIAIRE) ;</li>
                  <li>Nettoyer les vitres lorsque nécessaire, avec des produits adaptés.</li>
                </ul>
                <p className="mt-2"><b>2.1 Qualité</b> — trois critères : Aspect (netteté visuelle), Confort (suppression des odeurs, sécurité des produits) et Hygiène (assainissement).</p>
                <p className="mt-1"><b>2.2 Dispositions générales</b> — Eau et électricité fournies gracieusement par le logement. <b className="text-slate-800">Le PRESTATAIRE apporte tout son matériel et ses produits : aspirateur, microfibres, serpillère, seau, balai, chiffons, ainsi que tous les produits ménagers nécessaires (nettoyants multi-surfaces, dégraissants, détartrant, désinfectant, sacs poubelle, etc.).</b> Le coût est inclus dans le forfait. Prestations entre 10h et 16h selon planning <b>PlayMCB</b>. Entrées voyageurs à partir de 16h, départs à 10h. EPI personnels obligatoires.</p>
                <p className="mt-1"><b>2.3 Réglementation</b> — Le PRESTATAIRE respecte lois et règlements et garantit le BÉNÉFICIAIRE contre tout manquement.</p>
              </Article>

              <Article num="3" title="Tarification & Facturation">
                <p><b>3.1 Tarifs</b> — Forfait par mission établi via <b>PlayMCB</b>. Le prix TTC inclut main-d'œuvre, matériel, produits ménagers et EPI.</p>
                <p className="mt-1">Le montant du forfait est déterminé en fonction du <b>temps de prestation estimé</b> pour le logement, intégrant l'ensemble des tâches de l'article 2.</p>
                <p className="mt-2">Deux <b>catégories de logements</b> définies dans PlayMCB :</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>🟦 <b>Logements SANS linge</b> — Forfait couvrant exclusivement le nettoyage du logement.</li>
                  <li>🟧 <b>Logements AVEC linge</b> — Forfait couvrant <b>nettoyage ET prise en charge complète du linge</b> (changement, lavage, séchage, remise en place des draps, housses, taies, serviettes, tapis de bain, torchons). Temps de gestion du linge <b>réputé intégré dans le forfait</b> — aucune facturation séparée possible.</li>
                </ul>
                <p className="mt-2">Lorsque <b>2 FDM interviennent en équipe</b> sur un logement avec linge, la répartition de la part « linge » est définie dans PlayMCB (50/50 par défaut, ou personnalisée selon les profils).</p>
                <p className="mt-2 text-slate-500 text-xs">La non-exécution d'une prestation entraîne la non-facturation de la mission concernée.</p>
                <p className="mt-3"><b>3.2 Facturation</b> — Modalités détaillées dans l'onglet <b>« Mes Revenus »</b> de PlayMCB qui fait foi :</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Envoi à <b>facturation@maconciergerie-bretonne.fr</b></li>
                  <li>Règlement sous <b>15 jours ouvrés</b> après contrôle et éventuel remboursement voyageurs</li>
                  <li className="text-red-700"><b>Toute facture envoyée avant le 2 du mois est automatiquement rejetée.</b></li>
                  <li>Restitution obligatoire des draps et linges en fin de contrat (la facture de clôture n'est réglée qu'après confirmation).</li>
                </ul>
                <p className="mt-2"><b>3.3 Cas exceptionnels</b> — Prestations complémentaires ou modifications temporaires font l'objet d'un ajustement ponctuel via PlayMCB.</p>
              </Article>

              <Article num="4" title="Responsabilité & Assurances">
                <p><b>4.1 Responsabilités</b> — Le PRESTATAIRE est responsable de plein droit, à l'égard du BÉNÉFICIAIRE comme des tiers, des dommages causés en cours ou après intervention. Il garantit le BÉNÉFICIAIRE contre tous recours.</p>
                <p className="mt-2"><b>4.2 Assurances</b> — Le PRESTATAIRE souscrit une RC Pro et présente son attestation sur simple demande.</p>
              </Article>

              <Article num="5" title="Contrôle qualité">
                Contrôles synchrones ou asynchrones tracés dans <b>PlayMCB</b>. Toute anomalie (contrôle ou avis client) est partagée pour plan d'action correctif. En cas de manquement grave ou répété, résiliation possible selon §6.4.1.
              </Article>

              <Article num="6" title="Entrée en vigueur · Durée · Résiliation">
                <p><b>6.1</b> Entrée en vigueur à compter du <b>{contract?.contract_start_date || '—'}</b>.</p>
                <p className="mt-1"><b>6.2 Reconduction</b> — Aucune durée d'engagement minimum, sauf dénonciation par LRAR un mois avant ou annulation du mandat.</p>
                <p className="mt-1"><b>6.3 Rupture pour faute</b> — Résiliation immédiate après LRAR de mise en demeure restée infructueuse 8 jours.</p>
                <p className="mt-1"><b>6.4 Exécution forcée</b> — Après LRAR, le BÉNÉFICIAIRE peut faire exécuter les prestations par une autre entreprise aux frais du PRESTATAIRE.</p>
                <p className="mt-2"><b>6.4.1 Préavis de résiliation</b> selon nombre de logements gérés via PlayMCB :</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Moins de 10 logements : <b>2 mois</b></li>
                  <li>Entre 11 et 20 logements : <b>3 mois</b></li>
                  <li>Plus de 30 logements : <b>4 mois</b></li>
                </ul>
              </Article>

              <Article num="7" title="Sous-traitance">
                Sous-traitance interdite sauf accord préalable exprès. Tout manquement entraîne résiliation sans indemnité ni préavis. Contrat conclu intuitu personae : toute cessation ou modification de statut doit être notifiée par LRAR.
              </Article>

              <Article num="8" title="Non-concurrence & Non-sollicitation">
                Pendant la durée du contrat et pour <b>4 ans</b> après sa cessation, le PRESTATAIRE s'interdit de prendre contact avec les clients de MA CONCIERGERIE BRETONNE à des fins commerciales, et s'interdit d'échanger leurs coordonnées. Toute communication passe par le CITY MANAGER via PlayMCB.
              </Article>

              <Article num="9" title="Marque blanche">
                Aucun vêtement ou autocollant véhicule identifiant l'entreprise du PRESTATAIRE pendant les prestations. MA CONCIERGERIE BRETONNE peut fournir des t-shirts neutres à son nom.
              </Article>

              <Article num="10" title="Information">
                Le PRESTATAIRE communique au BÉNÉFICIAIRE toute information utile (légale, réglementaire ou d'intérêt opérationnel).
              </Article>

              <Article num="11" title="Confidentialité & Données">
                Aucune divulgation des informations connues via <b>PlayMCB</b> (coordonnées des logements, codes d'accès, codes interphones, photos, codes boîtes à clés, plannings, identités des clients/propriétaires). Obligation perdurant sans limitation de durée après cessation du contrat.
              </Article>

              <Article num="12" title="Indépendance · Absence de subordination">
                Les parties reconnaissent expressément que le contrat est conclu entre <b>deux professionnels indépendants</b>. Le PRESTATAIRE fixe librement son planning, accepte ou refuse les missions <b>sans avoir à se justifier</b>, n'est soumis à aucune exclusivité et conserve la liberté d'exercer pour d'autres clients (sous réserve de l'art. 8). Aucune subordination juridique, hiérarchique ou disciplinaire n'existe entre les parties. Le PRESTATAIRE déclare assumer seul ses obligations URSSAF, sociales et fiscales.
              </Article>

              <Article num="13" title="Documents obligatoires & vigilance URSSAF">
                Le PRESTATAIRE fournit, avant démarrage puis <b>tous les 6 mois</b> :
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Extrait <b>INSEE / KBIS</b> de moins de 3 mois</li>
                  <li><b>Attestation de vigilance URSSAF</b> de moins de 6 mois (art. L.243-15 CSS)</li>
                  <li><b>Attestation RC Pro</b> en cours de validité</li>
                </ul>
                <p className="mt-1">Information immédiate en cas de cessation, radiation, changement de statut ou dépassement des seuils micro-entrepreneur. À défaut : <b>suspension des paiements</b> et résiliation possible sans préavis.</p>
              </Article>

              <Article num="14" title="Dommages au logement, au mobilier ou aux effets des voyageurs">
                Responsabilité de plein droit du PRESTATAIRE en cas de dommage matériel pendant l'intervention. <b>Signalement sous 24h</b> via PlayMCB avec photos. À défaut, présomption de responsabilité. Le coût de remise en état peut être imputé sur les paiements ou recouvré directement. Couverture exigée par la RC Pro (§4.2).
              </Article>

              <Article num="15" title="Clés, boîtes à clés et codes d'accès">
                Le PRESTATAIRE est dépositaire des clés et codes confiés via PlayMCB. Toute <b>perte, vol, copie ou divulgation</b> doit être signalée immédiatement. Le coût de changement de serrure, re-codage ou dispositif est <b>intégralement à la charge du PRESTATAIRE</b>. Transmission à un tiers strictement interdite.
              </Article>

              <Article num="16" title="Réseaux sociaux, photos & communication">
                Sans accord écrit préalable du BÉNÉFICIAIRE, interdiction formelle de :
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Publier ou diffuser photos/vidéos des logements, voyageurs, propriétaires sur Instagram, Facebook, TikTok, X, Snapchat, LinkedIn, statuts WhatsApp, blogs, etc.</li>
                  <li>Utiliser les photos PlayMCB à des fins personnelles ou commerciales</li>
                  <li>Géolocaliser publiquement les logements</li>
                </ul>
                <p className="mt-1 text-red-700"><b>Manquement = résiliation immédiate</b> sans préavis ni indemnité, sans préjudice des dommages-intérêts.</p>
              </Article>

              <Article num="17" title="Protection des données (RGPD)">
                Le PRESTATAIRE agit en qualité de <b>sous-traitant au sens de l'art. 28 du RGPD</b> et s'engage à : traiter les données uniquement pour les besoins du contrat, ne rien exporter ni conserver au-delà du nécessaire, assurer la confidentialité absolue (y compris vis-à-vis de ses proches), notifier sans délai toute violation, <b>supprimer ou restituer</b> toutes les données à la cessation du contrat.
              </Article>

              <Article num="18" title="Comportement, présentation & bonnes pratiques">
                Pendant les prestations :
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Tenue propre, soignée, sans logo (cf. art. 9)</li>
                  <li><b>Hygiène personnelle irréprochable</b></li>
                  <li>Aucun alcool / tabac / cigarette électronique / substance illicite dans le logement ou aux abords</li>
                  <li>Aucun accompagnement (enfants, amis, famille, <b>animaux</b>) sans accord écrit préalable</li>
                  <li>Comportement <b>professionnel et courtois</b> envers voyageurs, propriétaires, voisins</li>
                </ul>
              </Article>

              <Article num="19" title="Indisponibilité · Désistement de mission">
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><b>Indisponibilité prévisible</b> (congés, RDV) : préavis ≥ <b>48h ouvrées</b> via PlayMCB</li>
                  <li><b>Urgence</b> (maladie, accident) : prévenir <b>avant 8h le jour-même</b> par moyen traçable</li>
                </ul>
                <p className="mt-1">Désistement injustifié ou &lt;24h sans motif : (i) refacturation des frais, (ii) avertissement formel, (iii) en cas de récidive, résiliation pour faute (§6.3).</p>
              </Article>

              <Article num="20" title="Pourboires & objets oubliés">
                <p><b>20.1 Pourboires</b> — Les pourboires en espèces laissés par les voyageurs appartiennent au PRESTATAIRE. Il en assume seul la <b>déclaration fiscale et sociale</b> au titre de son chiffre d'affaires (régime micro-entreprise).</p>
                <p className="mt-2"><b>20.2 Objets oubliés</b> — Tout objet, document, somme d'argent, bagage ou effet personnel découvert dans le logement doit être <b>signalé dans PlayMCB</b> (avec photo) et <b>remis au BÉNÉFICIAIRE sous 48h</b>. Interdiction formelle de conserver, utiliser, photographier à des fins personnelles, donner ou jeter ces objets.</p>
                <p className="mt-1 text-red-700"><b>Manquement = résiliation immédiate + poursuites pénales possibles.</b></p>
              </Article>

              <Article num="21" title="Élection de domicile">
                Les parties font élection de domicile en leur siège social respectif et s'informent mutuellement de tout changement.
              </Article>

              <Article num="22" title="Litiges">
                Toute contestation est traitée après tentative de règlement amiable. Compétence du Tribunal du lieu du siège social du défendeur.
              </Article>

            </div>
          </details>

          <p className="text-xs text-slate-500 mt-3">
            ⓘ La version complète au format PDF (avec votre signature) vous sera envoyée par e-mail dès signature.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6 mb-4">
          <h2 className="font-semibold text-slate-700 mb-3 text-sm uppercase tracking-wide">
            Consentement
          </h2>
          <label className="flex items-start gap-3 cursor-pointer mb-3">
            <Checkbox
              checked={agree1}
              onCheckedChange={setAgree1}
              data-testid="agree-1"
            />
            <span className="text-sm text-slate-700">
              Je déclare avoir <b>lu et compris l'intégralité des 22 articles</b> du contrat de prestation
              auto-entrepreneur (consultables ci-dessus ou via le PDF complet), et je les accepte sans réserve.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={agree2}
              onCheckedChange={setAgree2}
              data-testid="agree-2"
            />
            <span className="text-sm text-slate-700">
              J'accepte que ma signature électronique manuscrite ait la même valeur juridique qu'une
              signature manuscrite physique (loi Eidas, art. 1366 du Code civil).
            </span>
          </label>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6 mb-6">
          <h2 className="font-semibold text-slate-700 mb-3 text-sm uppercase tracking-wide">
            Signature manuscrite
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            Tracez votre signature dans le cadre ci-dessous (à la souris ou au doigt sur mobile).
          </p>
          <SignaturePad ref={padRef} disabled={submitting} />
        </div>

        <Button
          onClick={submit}
          disabled={submitting || !agree1 || !agree2}
          className="w-full h-14 text-base bg-blue-600 hover:bg-blue-700"
          data-testid="contract-sign-submit"
        >
          {submitting && <Loader2 className="h-5 w-5 mr-2 animate-spin" />}
          Signer le contrat
        </Button>

        <p className="text-center text-xs text-slate-400 mt-4">
          PlayMCB — MA CONCIERGERIE BRETONNE SAS. Document signé est horodaté et conservé.
        </p>
      </div>
    </div>
  );
}
